<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatAed, formatToInternational } from '@shared/utils'
import type { NextStep } from '../constants/investFlow'
import InvestNextSteps from './InvestNextSteps.vue'

// Order Confirmed (Figma 32590:68784): the tick, the order id, the asset
// line, the order documents (slot), what happens next, then the two exits.

interface Props {
  assetName: string
  tokens: number
  stakeLabel: string
  totalPaid: number
  orderReference: string
  nextSteps: NextStep[]
}

defineProps<Props>()

defineEmits<{
  (e: 'view-order'): void
  (e: 'back-to-properties'): void
}>()
</script>

<template>
  <Box direction="col" grow class="confirmed">
    <Box direction="col" align="center" gap="32px" class="confirmed__body">
      <Box direction="col" align="center" gap="6" class="confirmed__hero">
        <Box align="center" justify="center" class="confirmed__ring">
          <Box align="center" justify="center" class="confirmed__tick">
            <PiIcon name="check-circle" class="confirmed__tick-icon" />
          </Box>
        </Box>
        <Box direction="col" align="center" gap="20px">
          <Box direction="col" align="center" gap="2">
            <Text type="display" weight="semibold" align="center" is="h3">Order Confirmed</Text>
            <Text type="body" weight="medium" color="muted" align="center" class="confirmed__lead">
              Your commitment has been secured. The funds are currently held in escrow.
            </Text>
          </Box>
          <Box v-if="orderReference" align="center" class="confirmed__id">
            <Text type="caption" color="var(--p-surface-600)" is="span">ORDER ID:&nbsp;</Text>
            <Text type="caption" weight="semibold" color="default" is="span">{{
              orderReference
            }}</Text>
          </Box>
        </Box>
      </Box>

      <Box direction="col" gap="4" block>
        <Box align="center" justify="between" gap="3" class="confirmed__card">
          <Box align="center" gap="3" class="confirmed__card-main">
            <Box align="center" justify="center" class="confirmed__tile confirmed__tile--brand">
              <PiIcon name="building" class="confirmed__tile-icon confirmed__tile-icon--brand" />
            </Box>
            <Box direction="col" gap="1" class="confirmed__card-text">
              <Text type="body" weight="bold" is="span" truncate>{{ assetName }}</Text>
              <Text type="caption" weight="medium" color="muted" is="span">
                {{ formatToInternational(tokens) }} Tokens • {{ stakeLabel }} Stake
              </Text>
            </Box>
          </Box>
          <Text type="heading" weight="bold" is="span" style="white-space: nowrap"
            ><CurrencyText :text="formatAed(totalPaid)"
          /></Text>
        </Box>

        <!-- Order Receipt + Title Deed rows — owned by the orders module. -->
        <slot name="documents" />

        <InvestNextSteps :steps="nextSteps" />
      </Box>
    </Box>

    <Box direction="col" gap="3" class="confirmed__footer">
      <Button
        label="View Order Details"
        icon="arrow-right"
        icon-pos="right"
        size="large"
        block
        @click="$emit('view-order')"
      />
      <Button
        label="Back to Properties"
        color="secondary"
        variant="outline"
        size="large"
        block
        @click="$emit('back-to-properties')"
      />
    </Box>
  </Box>
</template>

<style scoped>
.confirmed {
  min-height: 100%;
}

.confirmed__body {
  flex: 1;
  padding: 38px 27px 24px;
}

.confirmed__ring {
  width: 80px;
  height: 80px;
  border: 2px solid var(--p-success-100);
  border-radius: 80px;
}

.confirmed__tick {
  width: 56px;
  height: 56px;
  border-radius: 40px;
  background: var(--p-emerald-50);
}

.confirmed__tick-icon {
  font-size: 28px;
  color: var(--p-success-600);
}

.confirmed__lead {
  max-width: 274px;
  line-height: 20px;
}

.confirmed__id {
  padding: 6px 12px;
  border: 1px solid var(--p-surface-200);
  border-radius: 26px;
  background: var(--p-surface-100);
}

.confirmed__card {
  width: 100%;
  padding: 17px;
  border: 1px solid var(--p-surface-100);
  border-radius: 20px;
  background: var(--p-surface-0);
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.03));
}

.confirmed__card-main,
.confirmed__card-text {
  min-width: 0;
}

.confirmed__tile {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: var(--p-surface-100);
}

.confirmed__tile--brand {
  background: var(--p-primary-50);
}

.confirmed__tile-icon {
  font-size: 18px;
  color: var(--p-surface-600);
}

.confirmed__tile-icon--brand {
  color: var(--p-primary-600);
}

.confirmed__footer {
  position: sticky;
  bottom: 0;
  padding: 10px 24px 20px;
  border-top: 1px solid var(--p-surface-200);
  background: var(--p-surface-50);
}
</style>
